import React from "react";
import {
    CheckCircle, HourglassHigh, Cube, Eye, WarningCircle, PencilLine,
    PaperPlaneTilt,
} from "@phosphor-icons/react";
import {
    STATUS_CONFIG, statusLabel, statusTone, statusDescription, productionStageLabel,
} from "@/lib/caseStatus";

/**
 * <CaseStatusBadge>
 *
 * Single, portal-agnostic case-status badge. Use this everywhere a case status
 * needs to be displayed — Admin, Doctor, Patient, COE, Production — so all
 * portals share the same colours, labels, and visual rhythm.
 *
 * The design intent is a "status capsule": a coloured icon disc on the left
 * paired with a bold label. In the `lg` size the capsule also surfaces a
 * short description underneath so the user understands the state without
 * needing to hover.
 *
 *   <CaseStatusBadge status={c.status} />                    // md (default)
 *   <CaseStatusBadge status={c.status} size="lg" />           // for page headers
 *   <CaseStatusBadge status={c.status} size="sm" />           // for table cells
 *   <CaseStatusBadge status={c.status} showDescription />     // force the strapline
 *   <CaseStatusBadge status={c.status} portal="doctor" />     // portal-specific label
 *
 * Tones map 1:1 to STATUS_CONFIG[status].tone — slate / amber / violet /
 * rose / emerald / sky.
 */

/* Tone → solid + tinted styling. Two layers so the icon disc has slightly
 * deeper saturation than the surrounding pill. */
const TONE_STYLES = {
    slate:   { pill: "bg-slate-100 text-slate-900 border-slate-200",     disc: "bg-slate-500/15 text-slate-700"    },
    amber:   { pill: "bg-amber-50 text-amber-900 border-amber-200",      disc: "bg-amber-500/20 text-amber-700"    },
    violet:  { pill: "bg-violet-50 text-violet-900 border-violet-200",   disc: "bg-violet-500/20 text-violet-700"  },
    rose:    { pill: "bg-rose-50 text-rose-900 border-rose-200",         disc: "bg-rose-500/20 text-rose-700"      },
    emerald: { pill: "bg-emerald-50 text-emerald-900 border-emerald-200",disc: "bg-emerald-500/20 text-emerald-700"},
    sky:     { pill: "bg-sky-50 text-sky-900 border-sky-200",            disc: "bg-sky-500/20 text-sky-700"        },
};

/* iter100.73 — Premium tone treatment for the `lg` status capsule.
 * Slim gradient pill + solid-gradient icon disc (white icon on
 * tone-500→600). Glossy top highlight via inset white shadow, subtle
 * elevation shadow underneath so the capsule feels lifted off the card
 * without being bulky. Compact height (~28px) reads far more premium
 * than the previous ~44px rounded-2xl block. */
const TONE_STYLES_PREMIUM = {
    slate:   { pill: "bg-gradient-to-b from-slate-50 to-slate-100/60 border-slate-200 text-slate-900",     disc: "bg-gradient-to-br from-slate-500 to-slate-700"     },
    amber:   { pill: "bg-gradient-to-b from-amber-50 to-amber-100/60 border-amber-200 text-amber-900",     disc: "bg-gradient-to-br from-amber-500 to-amber-600"     },
    violet:  { pill: "bg-gradient-to-b from-violet-50 to-violet-100/60 border-violet-200 text-violet-900", disc: "bg-gradient-to-br from-violet-500 to-violet-700"   },
    rose:    { pill: "bg-gradient-to-b from-rose-50 to-rose-100/60 border-rose-200 text-rose-900",         disc: "bg-gradient-to-br from-rose-500 to-rose-600"       },
    emerald: { pill: "bg-gradient-to-b from-emerald-50 to-emerald-100/60 border-emerald-200 text-emerald-900", disc: "bg-gradient-to-br from-emerald-500 to-emerald-700"},
    sky:     { pill: "bg-gradient-to-b from-sky-50 to-sky-100/60 border-sky-200 text-sky-900",             disc: "bg-gradient-to-br from-sky-500 to-sky-700"         },
};

/* Per-status icon. Falls back to the tone-default icon if unmapped. */
const STATUS_ICONS = {
    draft:                    PencilLine,
    submitted:                HourglassHigh,
    assigned_to_planner:      HourglassHigh,
    in_planning:              Cube,
    info_requested:           HourglassHigh,
    submitted_to_qa:          Eye,
    sent_for_approval:        PaperPlaneTilt,
    ready_for_review:         PaperPlaneTilt,
    revision_requested:       HourglassHigh,
    approved:                 CheckCircle,
    approved_pending_mfg:     HourglassHigh,
    awaiting_production_assignment: HourglassHigh,
    assigned_to_production:   Cube,
    submitted_to_production:  Cube,
    in_production:            Cube,
    shipped:                  PaperPlaneTilt,
    in_treatment:             HourglassHigh,
    treatment_started:        CheckCircle,
    refinement_requested:     HourglassHigh,
    completed:                CheckCircle,
    cancelled:                WarningCircle,
};

const TONE_DEFAULT_ICON = {
    slate:   PencilLine,
    amber:   HourglassHigh,
    violet:  Cube,
    rose:    WarningCircle,
    emerald: CheckCircle,
    sky:     PaperPlaneTilt,
};

const SIZE_STYLES = {
    sm: { pill: "px-2 py-0.5 gap-1.5 text-[10px] uppercase tracking-wider font-bold",  disc: "w-4 h-4",   icon: 10, descTextSize: "" },
    md: { pill: "px-2.5 py-1 gap-2 text-xs font-bold uppercase tracking-wider",        disc: "w-5 h-5",   icon: 12, descTextSize: "" },
    lg: { pill: "px-3 py-1.5 gap-2.5 text-sm font-bold",                               disc: "w-7 h-7",   icon: 16, descTextSize: "text-[11px]" },
};

export function CaseStatusBadge({
    status,
    size = "md",
    showDescription = false,
    className = "",
    portal,
    productionStage,
    "data-testid": dataTestId = "case-status-badge",
}) {
    const tone = statusTone(status);
    const toneStyle = TONE_STYLES[tone] || TONE_STYLES.slate;
    const Icon = STATUS_ICONS[status] || TONE_DEFAULT_ICON[tone] || HourglassHigh;
    const sizing = SIZE_STYLES[size] || SIZE_STYLES.md;
    /* When the case is in production AND the caller passed the current
       production_stage, surface the stage-level label (e.g. "Thermoforming")
       instead of the generic "Production Started" pill. This is opt-in;
       doctors/patients should NOT pass `productionStage` so they stay on
       the high-level label. */
    const stageOverride = status === "in_production" && productionStage
        ? productionStageLabel(productionStage)
        : "";
    const label = stageOverride || statusLabel(status, portal);
    const description = statusDescription(status);
    const cfg = STATUS_CONFIG[status];

    // Pulse the icon disc for "waiting on someone" states (amber tone) so the
    // user can spot at a glance which cases are blocked on a human action.
    const isWaiting = tone === "amber";

    /* ─── lg variant: premium compact status capsule ─────────────────
       iter100.73 — Redesigned per user direction (2026-02-28). Was a
       bulky ~44px rounded-2xl block; now a slim ~28px gradient pill
       with a solid gradient icon disc + glossy top highlight for a
       premium B2B SaaS feel. Waiting-state ping preserved on the icon
       disc so blocked-on-human states still catch the eye. */
    if (size === "lg") {
        const premium = TONE_STYLES_PREMIUM[tone] || TONE_STYLES_PREMIUM.slate;
        return (
            <div
                className={`inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border ${premium.pill} ${className}`}
                style={{
                    boxShadow: "0 1px 2px 0 rgba(15, 23, 42, 0.06), inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
                }}
                data-testid={dataTestId}
            >
                <div className={`relative rounded-full w-5 h-5 flex items-center justify-center shrink-0 ${premium.disc}`} style={{ boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.25)" }}>
                    <Icon size={11} weight="fill" className="text-white" />
                    {isWaiting && (
                        <span className="absolute -inset-0.5 rounded-full animate-ping bg-amber-400/40" aria-hidden />
                    )}
                </div>
                <div className="flex items-baseline gap-1 leading-none">
                    <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] opacity-55">Case Status</span>
                    <span className="opacity-30">·</span>
                    <span className="text-[12px] font-bold tracking-[0.02em] uppercase">{label}</span>
                </div>
                {showDescription && description && (
                    <div className="text-[11px] opacity-70 leading-none pl-1 border-l border-current/15 ml-1 normal-case font-medium">{description}</div>
                )}
            </div>
        );
    }

    /* ─── sm / md variants: compact pill with icon ────────────────────── */
    return (
        <span
            className={`inline-flex items-center rounded-full border ${toneStyle.pill} ${sizing.pill} ${className}`}
            data-testid={dataTestId}
        >
            <span className={`relative rounded-full ${toneStyle.disc} ${sizing.disc} inline-flex items-center justify-center shrink-0`}>
                <Icon size={sizing.icon} weight="fill" />
                {isWaiting && (
                    <span className="absolute inset-0 rounded-full animate-ping bg-amber-400/30" aria-hidden />
                )}
            </span>
            <span className="truncate">{label}</span>
        </span>
    );
}

export default CaseStatusBadge;
